import React, { useEffect, useMemo, useState } from 'react';
import {
  View,
  TouchableOpacity,
  ActivityIndicator,
  StyleSheet,
  SafeAreaView,
  Platform,
  Text,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';

const SEARCH_SCREEN_ROUTE = 'BrandListings';

export default function OfferMap({ navigation }) {
  const [mapData, setMapData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');

  useEffect(() => {
    let isMounted = true;

    const fetchMapData = async () => {
      try {
        setLoading(true);
        setFetchError('');

        const response = await fetch('https://collabbay.com/APIControllers/GetOfferMapData.php');
        const result = await response.json();

        if (!isMounted) return;

        if (result?.success && Array.isArray(result.data)) {
          setMapData(result.data);
        } else {
          setMapData([]);
          setFetchError(result?.error || 'Unable to load map data.');
        }
      } catch (error) {
        console.error('OfferMap fetch error:', error);
        if (!isMounted) return;
        setMapData([]);
        setFetchError('Unable to load map data.');
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchMapData();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'web') return;

    const handleMessage = (event) => {
      try {
        const payload =
          typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

        if (
          payload?.type === 'open_neighbourhood_offers' &&
          payload?.neighbourhood &&
          String(payload.neighbourhood).trim() !== ''
        ) {
          navigation.navigate(SEARCH_SCREEN_ROUTE, {
            selectedNeighbourhood: String(payload.neighbourhood).trim(),
            mapTapTs: Date.now(),
          });
          return;
        }

        if (
          payload?.type === 'open_nearby_offers' &&
          Array.isArray(payload.neighbourhoods) &&
          payload.neighbourhoods.length > 0
        ) {
          const cleanNeighbourhoods = payload.neighbourhoods
            .map((item) => String(item || '').trim())
            .filter(Boolean);

          if (cleanNeighbourhoods.length > 0) {
            navigation.navigate(SEARCH_SCREEN_ROUTE, {
              selectedNeighbourhoods: cleanNeighbourhoods,
              mapTapTs: Date.now(),
            });
          }
        }
      } catch (error) {
        // ignore malformed messages
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [navigation]);

  const mapHtml = useMemo(() => {
    const safeData = JSON.stringify(mapData).replace(/</g, '\\u003c');

    return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Offer Map</title>
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <link
    rel="stylesheet"
    href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"
    crossorigin=""
  />
  <style>
    :root {
      --bg: #eff3f6;
      --panel: rgba(255,255,255,0.92);
      --panel-border: rgba(17,17,17,0.08);
      --panel-shadow: 0 18px 40px rgba(0,0,0,0.10);
      --text: #111111;
      --muted: #5f6368;
      --accent: #FDF05D;
      --accent-border: #171717;
      --nearby: #ffffff;
      --nearby-text: #111111;
      --mydot: #ffffff;
      --mydot-ring: rgba(255,255,255,0.30);
    }

    html, body, #map {
      margin: 0;
      padding: 0;
      width: 100%;
      height: 100%;
      background: var(--bg);
      font-family: Inter, ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif;
      overflow: hidden;
    }

    .leaflet-container {
      background:
        radial-gradient(circle at top left, rgba(255,255,255,0.8), transparent 30%),
        linear-gradient(180deg, #eef2f5 0%, #e7edf1 100%);
    }

    .leaflet-control-attribution {
      font-size: 10px;
      background: rgba(255,255,255,0.84);
      border-radius: 12px 0 0 0;
      padding: 2px 8px;
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
    }

    .leaflet-control-zoom {
      border: none !important;
      box-shadow: none !important;
      margin-bottom: 22px !important;
      margin-right: 18px !important;
    }

    .leaflet-control-zoom a {
      width: 42px !important;
      height: 42px !important;
      line-height: 42px !important;
      border-radius: 14px !important;
      border: 1px solid var(--panel-border) !important;
      background: rgba(255,255,255,0.95) !important;
      color: #111 !important;
      box-shadow: var(--panel-shadow) !important;
      font-size: 18px !important;
      margin-bottom: 10px !important;
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
    }

    .leaflet-bar a:last-child {
      border-bottom-left-radius: 14px !important;
      border-bottom-right-radius: 14px !important;
    }

    .leaflet-bar a:first-child {
      border-top-left-radius: 14px !important;
      border-top-right-radius: 14px !important;
    }

    .leaflet-popup-content-wrapper {
      border-radius: 22px;
      background: rgba(255,255,255,0.97);
      border: 1px solid rgba(17,17,17,0.06);
      box-shadow: 0 24px 54px rgba(0,0,0,0.16);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
    }

    .leaflet-popup-content {
      margin: 14px;
      min-width: 200px;
    }

    .leaflet-popup-tip {
      background: rgba(255,255,255,0.97);
    }

    .map-ui-top {
      position: absolute;
      top: 16px;
      left: 16px;
      right: 16px;
      z-index: 999;
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      pointer-events: none;
      gap: 12px;
    }

    .map-ui-left,
    .map-ui-right {
      display: flex;
      flex-direction: column;
      gap: 10px;
      pointer-events: auto;
    }

    .glass-card {
      background: var(--panel);
      border: 1px solid var(--panel-border);
      box-shadow: var(--panel-shadow);
      border-radius: 20px;
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
    }

    .summary-card {
      padding: 12px 14px;
      min-width: 150px;
    }

    .summary-top {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      margin-bottom: 6px;
    }

    .summary-title {
      font-size: 12px;
      font-weight: 800;
      color: var(--muted);
      letter-spacing: 0.2px;
    }

    .summary-count {
      font-size: 22px;
      line-height: 1;
      font-weight: 900;
      color: var(--text);
      margin-bottom: 4px;
    }

    .summary-sub {
      font-size: 12px;
      color: var(--muted);
      font-weight: 700;
    }

    .action-btn {
      border: none;
      outline: none;
      cursor: pointer;
      padding: 12px 14px;
      border-radius: 18px;
      font-size: 13px;
      font-weight: 900;
      color: var(--text);
      display: inline-flex;
      align-items: center;
      gap: 8px;
      transition: transform 0.16s ease, box-shadow 0.16s ease, opacity 0.16s ease;
    }

    .action-btn:hover {
      transform: translateY(-1px);
    }

    .action-btn:active {
      transform: translateY(0);
      opacity: 0.92;
    }

    .action-primary {
      background: rgba(255,255,255,0.94);
      border: 1px solid var(--panel-border);
      box-shadow: var(--panel-shadow);
    }

    .action-dark {
      background: rgba(17,17,17,0.92);
      color: white;
      border: 1px solid rgba(255,255,255,0.08);
      box-shadow: 0 18px 40px rgba(0,0,0,0.18);
    }

    .map-bottom-floating {
      position: absolute;
      bottom: 18px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 999;
      pointer-events: none;
    }

    .hint-pill {
      background: rgba(17,17,17,0.84);
      color: white;
      border-radius: 999px;
      padding: 11px 14px;
      font-size: 12px;
      font-weight: 800;
      box-shadow: 0 16px 34px rgba(0,0,0,0.22);
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      white-space: nowrap;
    }

    .offer-marker-wrap {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      transform: translate(-50%, -50%);
      white-space: nowrap;
      cursor: pointer;
      user-select: none;
    }

    .offer-marker-bubble {
      display: flex;
      align-items: center;
      justify-content: center;
      background: radial-gradient(circle at 30% 30%, #fff6a7 0%, var(--accent) 55%, #f0dd2d 100%);
      color: #111;
      border: 2px solid var(--accent-border);
      box-shadow:
        0 16px 28px rgba(0,0,0,0.14),
        0 4px 8px rgba(0,0,0,0.08);
      font-weight: 900;
      line-height: 1;
      transition: transform 0.18s ease;
    }

    .offer-marker-wrap:hover .offer-marker-bubble {
      transform: scale(1.04);
    }

    .offer-marker-label {
      margin-top: 7px;
      background: rgba(255,255,255,0.96);
      color: #111;
      border: 1px solid rgba(17,17,17,0.08);
      border-radius: 999px;
      padding: 5px 10px;
      font-size: 10px;
      font-weight: 900;
      box-shadow: 0 10px 22px rgba(0,0,0,0.10);
      max-width: 132px;
      overflow: hidden;
      text-overflow: ellipsis;
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
    }

    .popup-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      margin-bottom: 6px;
    }

    .popup-title {
      font-size: 16px;
      font-weight: 900;
      color: #111;
      margin: 0;
    }

    .popup-count-pill {
      background: #f4f4f4;
      border: 1px solid rgba(17,17,17,0.08);
      color: #111;
      border-radius: 999px;
      padding: 5px 9px;
      font-size: 11px;
      font-weight: 900;
      white-space: nowrap;
    }

    .popup-sub {
      font-size: 13px;
      color: #555;
      margin-bottom: 12px;
      line-height: 1.35;
    }

    .popup-actions {
      display: flex;
      gap: 8px;
    }

    .popup-btn {
      flex: 1;
      border: none;
      outline: none;
      cursor: pointer;
      border-radius: 999px;
      padding: 11px 14px;
      font-size: 13px;
      font-weight: 900;
      transition: transform 0.16s ease, opacity 0.16s ease;
    }

    .popup-btn:hover {
      transform: translateY(-1px);
    }

    .popup-btn:active {
      transform: translateY(0);
      opacity: 0.92;
    }

    .popup-btn-primary {
      background: var(--accent);
      color: #111;
      box-shadow: inset 0 0 0 1.5px #111;
    }

    .popup-btn-secondary {
      background: #f3f3f3;
      color: #111;
      box-shadow: inset 0 0 0 1px rgba(17,17,17,0.08);
    }

    .my-location-wrap {
      transform: translate(-50%, -50%);
      pointer-events: none;
    }

    .my-location-dot {
      width: 18px;
      height: 18px;
      border-radius: 999px;
      background: var(--mydot);
      border: 3px solid #111;
      box-shadow:
        0 0 0 10px var(--mydot-ring),
        0 14px 30px rgba(0,0,0,0.18);
    }

    .my-location-pulse {
      width: 46px;
      height: 46px;
      margin-left: -14px;
      margin-top: -14px;
      border-radius: 999px;
      border: 1.5px solid rgba(255,255,255,0.55);
      background: rgba(255,255,255,0.12);
      animation: pulse 2s infinite;
    }

    @keyframes pulse {
      0% {
        transform: scale(0.75);
        opacity: 0.9;
      }
      70% {
        transform: scale(1.15);
        opacity: 0.18;
      }
      100% {
        transform: scale(1.25);
        opacity: 0;
      }
    }

    @media (max-width: 640px) {
      .map-ui-top {
        top: 14px;
        left: 14px;
        right: 14px;
      }

      .summary-card {
        padding: 10px 12px;
        min-width: 130px;
      }

      .summary-count {
        font-size: 19px;
      }

      .action-btn {
        padding: 11px 12px;
        font-size: 12px;
      }

      .hint-pill {
        font-size: 11px;
        padding: 10px 12px;
      }
    }
  </style>
</head>
<body>
  <div id="map"></div>

  <div class="map-ui-top">
    <div class="map-ui-left">
      <div class="glass-card summary-card">
        <div class="summary-top">
          <div class="summary-title">London offers</div>
          <div style="font-size:14px;">📍</div>
        </div>
        <div class="summary-count" id="offerTotalCount">0</div>
        <div class="summary-sub" id="offerTotalAreas">0 neighbourhoods</div>
      </div>
    </div>

    <div class="map-ui-right">
      <button class="action-btn action-primary" onclick="centerLondon()">Center map</button>
      <button class="action-btn action-dark" onclick="findNearbyOffers()">Click near me</button>
    </div>
  </div>

  <div class="map-bottom-floating">
    <div class="hint-pill">Tap a circle to view offers nearby</div>
  </div>

  <script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js" crossorigin=""></script>
  <script>
    const locations = ${safeData};

    const londonCenter = [51.5074, -0.1278];

    const map = L.map('map', {
      zoomControl: false,
      attributionControl: true
    }).setView(londonCenter, 11);

    L.control.zoom({ position: 'bottomright' }).addTo(map);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 19
    }).addTo(map);

    const bounds = [];
    let myMarker = null;
    let myAccuracyCircle = null;
    let myLat = null;
    let myLng = null;

    function escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }

    function openNeighbourhood(neighbourhood) {
      window.parent.postMessage(JSON.stringify({
        type: 'open_neighbourhood_offers',
        neighbourhood: neighbourhood
      }), '*');
    }

    function openNearby(neighbourhoods) {
      window.parent.postMessage(JSON.stringify({
        type: 'open_nearby_offers',
        neighbourhoods: neighbourhoods
      }), '*');
    }

    function centerLondon() {
      if (bounds.length > 0) {
        map.fitBounds(bounds, { padding: [56, 56] });
      } else {
        map.flyTo(londonCenter, 11, { duration: 0.85 });
      }
    }

    function setSummary() {
      const totalOffers = locations.reduce((sum, item) => {
        return sum + Number(item.OfferCount || 0);
      }, 0);

      document.getElementById('offerTotalCount').textContent = totalOffers;
      document.getElementById('offerTotalAreas').textContent =
        locations.length + (locations.length === 1 ? ' neighbourhood' : ' neighbourhoods');
    }

    function createOfferMarkerHtml(neighbourhood, count) {
      const safeNeighbourhood = escapeHtml(neighbourhood);
      const bubbleSize = count >= 20 ? 54 : count >= 10 ? 46 : count >= 5 ? 40 : 34;
      const fontSize = count >= 100 ? 13 : count >= 10 ? 12 : 11;

      return \`
        <div class="offer-marker-wrap">
          <div
            class="offer-marker-bubble"
            style="
              width:\${bubbleSize}px;
              height:\${bubbleSize}px;
              border-radius:\${bubbleSize}px;
              font-size:\${fontSize}px;
            "
          >
            \${count}
          </div>
          <div class="offer-marker-label">\${safeNeighbourhood}</div>
        </div>
      \`;
    }

    function makePopupHtml(neighbourhood, count) {
      const safeNeighbourhood = escapeHtml(neighbourhood);
      return \`
        <div>
          <div class="popup-head">
            <div class="popup-title">\${safeNeighbourhood}</div>
            <div class="popup-count-pill">\${count} offer\${count === 1 ? '' : 's'}</div>
          </div>
          <div class="popup-sub">Open this neighbourhood to see all live venue offers in the area.</div>
          <div class="popup-actions">
            <button class="popup-btn popup-btn-primary" onclick="openNeighbourhood('\${String(neighbourhood).replace(/'/g, "\\\\'")}')">View offers</button>
          </div>
        </div>
      \`;
    }

    function renderMyLocation(lat, lng, accuracy) {
      if (myMarker) {
        map.removeLayer(myMarker);
      }

      if (myAccuracyCircle) {
        map.removeLayer(myAccuracyCircle);
      }

      myLat = lat;
      myLng = lng;

      myAccuracyCircle = L.circle([lat, lng], {
        radius: Math.max(accuracy || 100, 80),
        color: '#ffffff',
        weight: 1.2,
        fillColor: '#ffffff',
        fillOpacity: 0.10
      }).addTo(map);

      const icon = L.divIcon({
        className: '',
        iconSize: [18, 18],
        iconAnchor: [9, 9],
        html: \`
          <div class="my-location-wrap">
            <div class="my-location-dot"></div>
          </div>
        \`
      });

      myMarker = L.marker([lat, lng], { icon }).addTo(map);
      myMarker.bindPopup('<div style="font-weight:900;color:#111;">Your location</div>');
    }

    function distanceInKm(lat1, lon1, lat2, lon2) {
      const toRad = (deg) => deg * Math.PI / 180;
      const R = 6371;
      const dLat = toRad(lat2 - lat1);
      const dLon = toRad(lon2 - lon1);

      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);

      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      return R * c;
    }

    function getNearestNeighbourhoods(lat, lng, maxCount = 3) {
      return locations
        .map(item => ({
          ...item,
          distance: distanceInKm(lat, lng, Number(item.Latitude), Number(item.Longitude))
        }))
        .filter(item => !Number.isNaN(item.distance))
        .sort((a, b) => a.distance - b.distance)
        .slice(0, maxCount)
        .map(item => item.Neighbourhood);
    }

    function findNearbyOffers() {
      if (!navigator.geolocation) {
        alert('Location is not supported on this browser.');
        return;
      }

      navigator.geolocation.getCurrentPosition(
        function(position) {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          const accuracy = position.coords.accuracy || 120;

          renderMyLocation(lat, lng, accuracy);
          map.flyTo([lat, lng], 13.3, { duration: 0.95 });

          const nearest = getNearestNeighbourhoods(lat, lng, 3);
          if (nearest.length > 0) {
            setTimeout(() => {
              openNearby(nearest);
            }, 700);
          }
        },
        function() {
          alert('Unable to access your current location.');
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    }

    locations.forEach(item => {
      const neighbourhood = item.Neighbourhood || '';
      const lat = Number(item.Latitude);
      const lng = Number(item.Longitude);
      const count = Number(item.OfferCount || 0);

      if (!neighbourhood || Number.isNaN(lat) || Number.isNaN(lng)) return;

      bounds.push([lat, lng]);

      const icon = L.divIcon({
        className: '',
        iconSize: [90, 62],
        iconAnchor: [45, 31],
        html: createOfferMarkerHtml(neighbourhood, count)
      });

      const marker = L.marker([lat, lng], { icon }).addTo(map);

      marker.bindPopup(makePopupHtml(neighbourhood, count), {
        closeButton: false,
        offset: [0, -8]
      });

      marker.on('click', function() {
        marker.openPopup();
      });
    });

    setSummary();

    if (bounds.length > 0) {
      map.fitBounds(bounds, { padding: [56, 56] });
    }
  </script>
</body>
</html>
    `;
  }, [mapData]);

  if (Platform.OS !== 'web') {
    return (
      <SafeAreaView style={styles.mobileFallback}>
        <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
          <Ionicons name="arrow-back" size={22} color="#000" />
        </TouchableOpacity>
        <View style={styles.mobileFallbackCenter}>
          <Text style={styles.mobileFallbackText}>Offer map is available on web.</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
        <Ionicons name="arrow-back" size={22} color="#000" />
      </TouchableOpacity>

      {loading ? (
        <View style={styles.loadingWrap}>
          <ActivityIndicator size="large" color="#000" />
        </View>
      ) : fetchError ? (
        <View style={styles.loadingWrap}>
          <Text style={styles.errorText}>{fetchError}</Text>
        </View>
      ) : (
        <iframe
          title="Collabbay Offer Map"
          srcDoc={mapHtml}
          style={styles.iframe}
        />
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#eef2f5',
  },
  mobileFallback: {
    flex: 1,
    backgroundColor: '#fff',
  },
  mobileFallbackCenter: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 20,
  },
  mobileFallbackText: {
    fontSize: 16,
    fontWeight: '700',
    color: '#111',
    textAlign: 'center',
  },
  backButton: {
    position: 'absolute',
    top: 18,
    left: 18,
    zIndex: 9999,
    width: 44,
    height: 44,
    borderRadius: 999,
    backgroundColor: 'rgba(255,255,255,0.98)',
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.08)',
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.12,
    shadowRadius: 22,
    elevation: 6,
  },
  loadingWrap: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorText: {
    fontSize: 15,
    color: '#111',
    fontWeight: '700',
  },
  iframe: {
    width: '100%',
    height: '100%',
    borderWidth: 0,
    border: 'none',
    display: 'block',
  },
});